import React from 'react';
import { useLanguage } from '../services/languageService';
import { zhCNContent } from '../i18n/translations/zh-CN-content';
import { zhTWContent } from '../i18n/translations/zh-TW-content';
import { jaContent } from '../i18n/translations/ja-content';
import { koContent } from '../i18n/translations/ko-content';
import { esContent } from '../i18n/translations/es-content';
import { enContent } from '../i18n/translations/en-content';
import { frContent } from '../i18n/translations/fr-content';
import { ptContent } from '../i18n/translations/pt-content';
import { arContent } from '../i18n/translations/ar-content';
import { deContent } from '../i18n/translations/de-content';
import { ruContent } from '../i18n/translations/ru-content';
import { idContent } from '../i18n/translations/id-content';
import { trContent } from '../i18n/translations/tr-content';
import { viContent } from '../i18n/translations/vi-content';
import { hiContent } from '../i18n/translations/hi-content';

// Import new content files for pokemonRandomizer
import { pokemonRandomizerContent as enPokemonRandomizerContent } from '../i18n/translations/en-pokemon-randomizer-content';
import { pokemonRandomizerContent as zhCNPokemonRandomizerContent } from '../i18n/translations/zh-CN-pokemon-randomizer-content';
import { pokemonRandomizerContent as zhTWPokemonRandomizerContent } from '../i18n/translations/zh-TW-pokemon-randomizer-content';
import { pokemonRandomizerContent as arPokemonRandomizerContent } from '../i18n/translations/ar-pokemon-randomizer-content';
import { pokemonRandomizerContent as dePokemonRandomizerContent } from '../i18n/translations/de-pokemon-randomizer-content';
import { pokemonRandomizerContent as esPokemonRandomizerContent } from '../i18n/translations/es-pokemon-randomizer-content';
import { pokemonRandomizerContent as frPokemonRandomizerContent } from '../i18n/translations/fr-pokemon-randomizer-content';
import { pokemonRandomizerContent as hiPokemonRandomizerContent } from '../i18n/translations/hi-pokemon-randomizer-content';
import { pokemonRandomizerContent as idPokemonRandomizerContent } from '../i18n/translations/id-pokemon-randomizer-content';
import { pokemonRandomizerContent as jaPokemonRandomizerContent } from '../i18n/translations/ja-pokemon-randomizer-content';
import { pokemonRandomizerContent as koPokemonRandomizerContent } from '../i18n/translations/ko-pokemon-randomizer-content';
import { pokemonRandomizerContent as ptPokemonRandomizerContent } from '../i18n/translations/pt-pokemon-randomizer-content';
import { pokemonRandomizerContent as ruPokemonRandomizerContent } from '../i18n/translations/ru-pokemon-randomizer-content';
import { pokemonRandomizerContent as trPokemonRandomizerContent } from '../i18n/translations/tr-pokemon-randomizer-content';
import { pokemonRandomizerContent as viPokemonRandomizerContent } from '../i18n/translations/vi-pokemon-randomizer-content';

// More flexible types for list items
interface BaseContentListItem {
  type: string; // Keep as string for flexibility
}
interface TextListItem extends BaseContentListItem {
  type: 'text';
  content: string;
}
interface FAQListItem extends BaseContentListItem {
  type: 'faq';
  question: string;
  answer: string;
}
type ContentListItem = TextListItem | FAQListItem | { type: string; [key: string]: any }; // Allow other types

// More flexible type for list containers
interface BaseContentItem {
  type: string; // Keep as string
  items?: ContentListItem[];
}
interface ListContentItem extends BaseContentItem {
  type: 'orderedList' | 'unorderedList';
}
interface FAQListContentItem extends BaseContentItem {
  type: 'faqList';
}
type ContentItem = ListContentItem | FAQListContentItem | { type: string; [key: string]: any }; // Allow other types

// Type for a section's data
interface SectionData {
  content: Array<string | ContentItem>;
  [key: string]: any; // Allow other potential properties if structures differ
}

// Type for a complete content module (like enContent or enPokemonRandomizerContent)
type ContentModule = {
  [key: string]: SectionData;
};

// Type for the map holding different language modules
type ContentTypeMap = {
  [key: string]: ContentModule; // Key is language code
};

interface StaticContentProps {
  section: string; // Key of the section within a ContentModule
  className?: string;
  contentType?: 'default' | 'pokemonRandomizer';
}

/**
 * 静态内容组件
 * 用于渲染多语言静态内容
 * @param section - 内容部分的键名
 * @param className - 可选的CSS类名
 */
const StaticContent: React.FC<StaticContentProps> = ({ section, className = '', contentType = 'default' }) => {
  const { currentLanguage } = useLanguage();
  
  const getContent = (): SectionData | undefined => {
    const defaultContentMap: ContentTypeMap = {
      'zh-CN': zhCNContent,
      'zh-TW': zhTWContent,
      'ja': jaContent,
      'ko': koContent,
      'es': esContent,
      'en': enContent,
      'fr': frContent,
      'pt': ptContent,
      'ar': arContent,
      'de': deContent,
      'ru': ruContent,
      'id': idContent,
      'tr': trContent,
      'vi': viContent,
      'hi': hiContent,
    };

    const randomizerContentMap: ContentTypeMap = {
      'en': enPokemonRandomizerContent,
      'zh-CN': zhCNPokemonRandomizerContent,
      'zh-TW': zhTWPokemonRandomizerContent,
      'ar': arPokemonRandomizerContent,
      'de': dePokemonRandomizerContent,
      'es': esPokemonRandomizerContent,
      'fr': frPokemonRandomizerContent,
      'hi': hiPokemonRandomizerContent,
      'id': idPokemonRandomizerContent,
      'ja': jaPokemonRandomizerContent,
      'ko': koPokemonRandomizerContent,
      'pt': ptPokemonRandomizerContent,
      'ru': ruPokemonRandomizerContent,
      'tr': trPokemonRandomizerContent,
      'vi': viPokemonRandomizerContent,
    };

    const selectedContentSourceMap = contentType === 'pokemonRandomizer' ? randomizerContentMap : defaultContentMap;
    const fallbackContentSourceMap = contentType === 'pokemonRandomizer' ? randomizerContentMap : defaultContentMap;
    const ultimateFallbackModule = enContent; // Fallback to default English content
    const randomizerFallbackModule = enPokemonRandomizerContent; // Fallback for randomizer content

    let langKey = currentLanguage;
    let contentModule = selectedContentSourceMap[langKey];

    // If current language content is not found, try English for the selected type
    if (!contentModule) {
      langKey = 'en';
      contentModule = selectedContentSourceMap[langKey];
    }

    // If English content for the selected type is still not found, use the ultimate fallback
    if (!contentModule) {
       contentModule = contentType === 'pokemonRandomizer' ? randomizerFallbackModule : ultimateFallbackModule;
    }
    
    // Now, try to get the specific section
    let specificSectionData = contentModule?.[section];
    
    // If section is not found in the determined module, try the type-specific fallback (English)
    if(!specificSectionData && contentType === 'pokemonRandomizer' && randomizerFallbackModule){        
        specificSectionData = randomizerFallbackModule[section];
    }
    // If section is still not found, try the ultimate fallback (default English)
    if(!specificSectionData && ultimateFallbackModule){        
        specificSectionData = ultimateFallbackModule[section];
    }

    return specificSectionData;
  };

  const currentSectionData = getContent();

  if (!currentSectionData || !Array.isArray(currentSectionData.content)) {
    console.warn(`StaticContent: Content array not found for section '${section}', type '${contentType}', lang '${currentLanguage}'.`);
    return <div className={className}>Content not found for section: {section}</div>;
  }

  // Render functions need to be robust against unexpected types if flexibility is added
  const renderListItem = (item: ContentListItem): React.ReactNode => {
    if (item.type === 'text' && typeof (item as TextListItem).content === 'string') {
      return (item as TextListItem).content;
    }
    console.warn("StaticContent: Unsupported list item type or structure", item);
    return null;
  };

  const renderFAQItem = (item: ContentListItem): React.ReactNode => {
    if (item.type === 'faq' && typeof (item as FAQListItem).question === 'string' && typeof (item as FAQListItem).answer === 'string') {
      return (
        <div className="mb-4">
          <h3 className="font-semibold text-lg mb-2">{(item as FAQListItem).question}</h3>
          <p>{(item as FAQListItem).answer}</p>
        </div>
      );
    }
    console.warn("StaticContent: Unsupported FAQ item type or structure", item);
    return null;
  };

  const renderContentItem = (item: string | ContentItem, index: number): React.ReactNode => {
    if (typeof item === 'string') {
      // Replace markdown bold with strong tags for basic formatting
      const formattedItem = item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p className="mb-3" dangerouslySetInnerHTML={{ __html: formattedItem }} key={`p-${index}`}></p>;
    }

    if (item.type === 'orderedList' && Array.isArray(item.items)) {
      return (
        <ol className="list-decimal pl-6 mb-4 space-y-2" key={`ol-${index}`}>
          {item.items.map((listItem, liIndex) => (
            <li key={liIndex}>{renderListItem(listItem)}</li>
          ))}
        </ol>
      );
    }

    if (item.type === 'unorderedList' && Array.isArray(item.items)) {
      return (
        <ul className="list-disc pl-6 mb-4 space-y-2" key={`ul-${index}`}>
          {item.items.map((listItem, liIndex) => (
            <li key={liIndex}>{renderListItem(listItem)}</li>
          ))}
        </ul>
      );
    }

    if (item.type === 'faqList' && Array.isArray(item.items)) {
      return (
        <div className="space-y-4" key={`faq-${index}`}>
          {item.items.map((faqItem, faqIndex) => (
            <React.Fragment key={faqIndex}>
              {renderFAQItem(faqItem)}
            </React.Fragment>
          ))}
        </div>
      );
    }

    console.warn("StaticContent: Unsupported content item type or structure", item);
    return null;
  };

  return (
    <div className={className}>
      {currentSectionData.content.map((item, index) => (
         renderContentItem(item, index)
      ))}
    </div>
  );
};

export default StaticContent; 